/* eslint-disable react-hooks/exhaustive-deps */
"use client"

import { useState, useRef } from "react"
import { Row, Col, Button, Table } from "react-bootstrap"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import { Chart } from "react-chartjs-2"
import "bootstrap/dist/css/bootstrap.min.css"
import { formatNumber } from "../../utils/data"
import { useSelector } from "react-redux"
import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend)

const ChartAndTable = ({
    selectedCoin,
    selectedItem,
    startDate,
    endDate,
}) => {
    const chartRef = useRef()
    const cashFlow = useSelector((state) => state.cashFlow);

    const [showGrid, setShowGrid] = useState(false);
    const coinData = cashFlow?.coins?.find(c => c.CodMonedaInternacional === selectedCoin);
    const rowData = cashFlow?.cashFlowPcgItemsResume?.find(c => c.id === selectedItem);
    let title = `Flujo de Caja del ${startDate} al ${endDate} (${coinData?.Descripcion || ''})`;
    if (selectedItem) {
        title += `\n ${rowData?.description}`
    }

    const downloadExcel = () => {
        const sufijo = selectedCoin;
        const sufijoCxC = `Costo${sufijo}xC`;
        const sufijoCxP = `Costo${sufijo}xP`;
        const sufijoInicial = `Inicial${sufijo}`;
        const sufijoSaldo = `Saldo${sufijo}`;
        
        const dataForExcel = cashFlow.cashFlowData.map(item => ({
            Periodo: item.Periodo,
            Inicial: item[sufijoInicial],
            Ingresos: item[sufijoCxC],
            Egresos: item[sufijoCxP],
            Saldo: item[sufijoSaldo]
        }));
        
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(dataForExcel);
        
        const headerStyle = {
            fill: {
                fgColor: { rgb: "D3D3D3" }
            },
            font: {
                bold: true
            }
        };
        
        const range = XLSX.utils.decode_range(ws['!ref']);
        for(let C = range.s.c; C <= range.e.c; ++C) {
            const cellAddress = XLSX.utils.encode_cell({c: C, r: 0});
            if(!ws[cellAddress]) continue;
            
            ws[cellAddress].s = headerStyle;
        }
        
        const colWidths = [
            {wch: 15}, 
            {wch: 20},
            {wch: 20},
            {wch: 20},
            {wch: 20},
        ];
        ws['!cols'] = colWidths;
        
        XLSX.utils.book_append_sheet(wb, ws, "Flujo de Caja");
        
        const fileName = `${title.replace(/\n/g, ' ')}.xlsx`;
        
        XLSX.writeFile(wb, fileName);
    };

    const downloadPDF = () => {
        if (chartRef.current && chartRef.current.canvas) {
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            
            const originalCanvas = chartRef.current.canvas;
            
            tempCanvas.width = originalCanvas.width * 1.5;
            tempCanvas.height = originalCanvas.height;
            
            tempCtx.fillStyle = 'white';
            tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            
            const xOffset = (tempCanvas.width - originalCanvas.width) / 2;
            tempCtx.drawImage(originalCanvas, xOffset, 0);
            
            const imgData = tempCanvas.toDataURL('image/png', 1.0);
            
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'letter'
            });
            
            const margin = 15;
            const pdfWidth = pdf.internal.pageSize.getWidth() - (margin * 2);
            const pdfHeight = pdf.internal.pageSize.getHeight() - (margin * 2);
            
            const imgWidth = tempCanvas.width;
            const imgHeight = tempCanvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const finalWidth = imgWidth * ratio;
            const finalHeight = imgHeight * ratio;
            
            const x = margin + (pdfWidth - finalWidth) / 2;
            const y = margin + 15;
            
            pdf.setFontSize(14);
            pdf.setFont(undefined, 'bold');
            const titleLines = pdf.splitTextToSize(title.replace(/\n/g, ' - '), pdfWidth);
            pdf.text(titleLines, pdf.internal.pageSize.getWidth() / 2, margin + 10, { align: 'center' });
            
            pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
            
            pdf.save(`${title.replace(/\n/g, ' ')}.pdf`);
        }
    };

    const loadData = (monedaSeleccionada) => {
        const sufijo = monedaSeleccionada;
        const sufijoCxC = `Costo${sufijo}xC`;
        const sufijoCxP = `Costo${sufijo}xP`;
        const sufijoInicial = `Inicial${sufijo}`;
        const sufijoSaldo = `Saldo${sufijo}`;
        
        const periods = cashFlow?.cashFlowData?.map(item => item.Periodo);
        
        return {
            labels: periods,
            datasets: [
                {
                type: "bar",
                label: "Ingresos",
                data: cashFlow?.cashFlowData?.map(item => item[sufijoCxC] || 0),
                backgroundColor: "#FF8C42",
                borderColor: "#FF8C42",
                borderWidth: 1,
                yAxisID: 'y', // Asignar al eje izquierdo
                },
                {
                type: "bar",
                label: "Egresos",
                data: cashFlow?.cashFlowData?.map(item => item[sufijoCxP] || 0),
                backgroundColor: "#2E8B57",
                borderColor: "#2E8B57",
                borderWidth: 1,
                yAxisID: 'y', // Asignar al eje izquierdo
                },
                {
                type: "line",
                label: "Inicial",
                data: cashFlow?.cashFlowData?.map(item => item[sufijoInicial] || 0),
                borderColor: "#90EE90",
                backgroundColor: "#90EE90",
                borderWidth: 2,
                fill: false,
                yAxisID: 'y1', // Asignar al eje derecho
                },
                {
                type: "line",
                label: "Saldo",
                data: cashFlow?.cashFlowData?.map(item => item[sufijoSaldo] || 0),
                borderColor: "#8A2BE2",
                backgroundColor: "#8A2BE2",
                borderWidth: 2,
                fill: false,
                yAxisID: 'y1', // Asignar al eje derecho
                },
            ],
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
        legend: {
            position: "bottom",
            labels: {
            usePointStyle: true,
            padding: 20,
            },
        },
        title: {
            display: true,
            text: [
              `Flujo de Caja del ${startDate} al ${endDate} (${coinData?.Descripcion || ''})`,
              selectedItem ? rowData?.description : ''
            ].filter(Boolean),
            font: {
                weight: "bold",
                size: 12,
            },
            color: "#666",
            padding: {
              top: 10,
              bottom: 10
            }
        },
        tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
                // Formatear las etiquetas del tooltip
                label: function(context) {
                    const label = context.dataset.label || '';
                    const value = context.parsed.y;
                    
                    // Formatear el valor usando tu función formatNumber
                    const formattedValue = formatNumber(value);
                    
                    // Para el modo de múltiples datasets, Chart.js muestra todas las etiquetas juntas
                    return `${label}: ${formattedValue}`;
                },
                // También puedes formatear el título del tooltip si es necesario
                title: function(tooltipItems) {
                    // tooltipItems es un array con todos los elementos del tooltip
                    // Devuelve el período como título
                    if (tooltipItems.length > 0) {
                        return `Periodo: ${tooltipItems[0].label}`;
                    }
                    return '';
                },
                // Opcional: mostrar totales en el tooltip
                // afterBody: function(context) {
                //     // Calcular total de ingresos y egresos para el período
                //     const ingresos = context.find(item => item.dataset.label === 'Ingresos');
                //     const egresos = context.find(item => item.dataset.label === 'Egresos');
                    
                //     if (ingresos && egresos) {
                //         const neto = ingresos.parsed.y - egresos.parsed.y;
                //         return `Neto: ${formatNumber(neto)}`;
                //     }
                //     return '';
                // }
            },
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            titleColor: '#333',
            bodyColor: '#666',
            borderColor: '#ddd',
            borderWidth: 1,
        },
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: true,
                    color: "#e0e0e0",
                },
                ticks: {
                    // Si deseas formatear también los ticks del eje X
                    callback: function(value, index, values) {
                        // Aquí podrías formatear las fechas si es necesario
                        return this.getLabelForValue(value);
                    }
                }
            },
            // Escala izquierda para barras (Ingresos y Egresos)
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Ingresos / Egresos'
                },
                grid: {
                display: true,
                color: "#e0e0e0",
                },
                ticks: {
                    // Formatear los ticks del eje izquierdo
                    callback: function(value) {
                        return formatNumber(value);
                    }
                }
            },
            // Escala derecha para líneas (Inicial y Saldo)
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Inicial / Saldo'
                },
                grid: {
                display: false, // Ocultar grid del eje derecho para mejor visualización
                },
                grace: '5%',
                ticks: {
                    // Formatear los ticks del eje derecho
                    callback: function(value) {
                        return formatNumber(value);
                    }
                }
            },
        },
        interaction: {
            mode: "nearest",
            axis: "x",
            intersect: false,
        },
    }


    return (
        <div 
            className="chart-container" 
            style={{ 
                height: showGrid ? '100%' : '90%',
                width: '100%', 
                paddingTop: showGrid ? '3em' : '0',
            }}
        >
            {!showGrid && (
                <Row className="flex-grow-1 position-relative" style={{ minHeight: '0' }}>
                    <Col className="d-flex align-items-end justify-content-end gap-2">
                        <Button
                            variant="outline-primary" 
                            size="sm"
                            onClick={downloadPDF}
                            className="d-flex align-items-center"
                            style={{marginRight: '1em'}}
                        >
                            Descargar PDF
                        </Button>
                        <Button
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => setShowGrid(!showGrid)}
                            className="d-flex align-items-center"
                        >
                            Ver grilla
                        </Button>
                    </Col>
                </Row>
            )}
        
            {showGrid && cashFlow?.cashFlowData && (
                <div 
                    className="bg-white p-3 rounded shadow"
                    style={{
                        position: 'relative',
                        overflow: 'auto',
                        height: '100%'
                    }}
                >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0" style={{fontSize: '12px', whiteSpace: 'pre-line', textAlign: 'center'}}>{title}</h5>
                        <div className="d-flex gap-2">
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={downloadExcel}
                                style={{marginRight: '1em'}}
                            >
                                Descargar Excel
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={() => setShowGrid(false)}
                            >
                                Ver gráfico
                            </Button>
                        </div>
                    </div>
                    
                    <div className="table-responsive" style={{ maxHeight: 'calc(100% - 50px)' }}>
                        <Table striped bordered hover size="sm">
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                            <tr>
                            <th>Periodo</th>
                            <th style={{textAlign: 'end'}}>Inicial</th>
                            <th style={{textAlign: 'end'}}>Ingresos</th>
                            <th style={{textAlign: 'end'}}>Egresos</th>
                            <th style={{textAlign: 'end'}}>Saldo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cashFlow.cashFlowData.map((item, index) => {
                            const sufijo = selectedCoin;
                            const sufijoCxC = `Costo${sufijo}xC`;
                            const sufijoCxP = `Costo${sufijo}xP`;
                            const sufijoInicial = `Inicial${sufijo}`;
                            const sufijoSaldo = `Saldo${sufijo}`;
                            
                            return (
                                <tr key={index}>
                                <td>{item.Periodo}</td>
                                <td style={{textAlign: 'end'}}>{formatNumber(item[sufijoInicial])}</td>
                                <td style={{textAlign: 'end'}}>{formatNumber(item[sufijoCxC])}</td>
                                <td style={{textAlign: 'end'}}>{formatNumber(item[sufijoCxP])}</td>
                                <td style={{textAlign: 'end'}}>{formatNumber(item[sufijoSaldo])}</td>
                                </tr>
                            );
                            })}
                        </tbody>
                        </Table>
                    </div>
                </div>
            )}

            {!showGrid && <Chart
                ref={chartRef}
                type="bar"
                data={loadData(selectedCoin)} 
                options={chartOptions}
            />}
        </div>
    )
}

export default ChartAndTable